/**
 * Geometría de corte (IPP/IOP) para Reference Lines en PREVIEW.
 * El manifest preview suele traer solo URLs WebP; los tags espaciales viven en el DICOM.
 */
import dicomParser from 'dicom-parser'
import { DICOM_OFFLINE_CACHE_ENABLED, getCachedInstance, toArrayBuffer } from '../db/dicomCache'
import {
  copyGeometryOnto,
  parseVec,
  readFileIpp,
  readFileIop,
  readFileFoR,
} from './seriesLink'

function add3(a, b) {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}

function scale3(a, s) {
  return [a[0] * s, a[1] * s, a[2] * s]
}

function len3(a) {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2])
}

function normalize3(a) {
  const l = len3(a)
  if (!(l > 1e-9)) return null
  return [a[0] / l, a[1] / l, a[2] / l]
}

/**
 * @param {ArrayBuffer|Uint8Array|null|undefined} buffer
 * @returns {object|null}
 */
export function parseSliceGeometryFromDicomBuffer(buffer) {
  if (!buffer) return null
  try {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
    const ds = dicomParser.parseDicom(bytes, { untilTag: 'x7fe00010' })
    const ipp = parseVec(ds.string('x00200032'), 3)
    const iop = parseVec(ds.string('x00200037'), 6)
    if (!ipp || !iop) return null
    const pixelSpacing = parseVec(ds.string('x00280030'), 2)
    const sliceThicknessRaw = ds.string('x00180050')
    const sliceThickness =
      sliceThicknessRaw != null && sliceThicknessRaw !== ''
        ? Number(sliceThicknessRaw)
        : null
    const rows = Number(ds.uint16('x00280010'))
    const columns = Number(ds.uint16('x00280011'))
    const frameOfReferenceUID = ds.string('x00200052') || null
    const out = {
      imagePositionPatient: ipp.slice(0, 3),
      imageOrientationPatient: iop.slice(0, 6),
    }
    if (frameOfReferenceUID) out.frameOfReferenceUID = String(frameOfReferenceUID).trim()
    if (pixelSpacing) out.pixelSpacing = pixelSpacing.slice(0, 2)
    if (Number.isFinite(sliceThickness)) out.sliceThickness = sliceThickness
    if (Number.isFinite(rows) && rows > 0) out.rows = rows
    if (Number.isFinite(columns) && columns > 0) out.columns = columns
    return out
  } catch (e) {
    return null
  }
}

/**
 * @param {object|null|undefined} file
 * @returns {boolean}
 */
export function fileNeedsGeometryHydration(file) {
  if (!file) return false
  return !readFileIpp(file) || !readFileIop(file)
}

/**
 * @param {object|null|undefined} file
 * @param {string|null|undefined} [archiveUrlFallback]
 * @returns {boolean}
 */
export function canHydratePreviewFileGeometry(file, archiveUrlFallback = null) {
  if (!file) return false
  const archiveUrl =
    (file.archiveUrl && String(file.archiveUrl).trim()) ||
    (archiveUrlFallback && String(archiveUrlFallback).trim()) ||
    null
  const offset = Number(file.byteOffset)
  const length = Number(file.byteLength)
  if (
    archiveUrl &&
    Number.isFinite(offset) &&
    offset >= 0 &&
    Number.isFinite(length) &&
    length > 0
  ) {
    return true
  }
  const dicomUrl = file.dicomUrl && String(file.dicomUrl).trim()
  return !!(dicomUrl && /^https?:\/\//i.test(dicomUrl))
}

/**
 * @param {object} file
 * @param {string|null|undefined} archiveUrlFallback
 * @returns {Promise<ArrayBuffer|null>}
 */
export async function fetchPreviewFileDicomBytes(file, archiveUrlFallback = null) {
  if (!file) return null

  if (DICOM_OFFLINE_CACHE_ENABLED && file.sopInstanceUID) {
    try {
      const row = await getCachedInstance(String(file.sopInstanceUID))
      if (row && row.status === 'complete' && row.blob) {
        const buffer = await toArrayBuffer(row.blob)
        if (buffer && buffer.byteLength > 0) return buffer
      }
    } catch (e) {
      /* ignore */
    }
  }

  const archiveUrl =
    (file.archiveUrl && String(file.archiveUrl).trim()) ||
    (archiveUrlFallback && String(archiveUrlFallback).trim()) ||
    null
  const offset = Number(file.byteOffset)
  const length = Number(file.byteLength)
  if (
    archiveUrl &&
    Number.isFinite(offset) &&
    offset >= 0 &&
    Number.isFinite(length) &&
    length > 0
  ) {
    try {
      const start = Math.floor(offset)
      const end = start + Math.floor(length) - 1
      const res = await fetch(String(archiveUrl), {
        method: 'GET',
        credentials: 'omit',
        headers: { Range: `bytes=${start}-${end}` },
      })
      if (res.ok || res.status === 206) {
        const buffer = await res.arrayBuffer()
        if (buffer && buffer.byteLength > 0) return buffer
      }
    } catch (e) {
      /* ignore */
    }
  }

  const dicomUrl = file.dicomUrl && String(file.dicomUrl).trim()
  if (dicomUrl && /^https?:\/\//i.test(dicomUrl)) {
    try {
      const res = await fetch(dicomUrl, { method: 'GET', credentials: 'omit' })
      if (res.ok) {
        const buffer = await res.arrayBuffer()
        if (buffer && buffer.byteLength > 0) return buffer
      }
    } catch (e) {
      /* ignore */
    }
  }

  return null
}

/**
 * @param {object} file
 * @param {object} geometry
 * @returns {boolean}
 */
export function stampGeometryOnPreviewFile(file, geometry) {
  if (!file || !geometry) return false
  copyGeometryOnto(file, geometry)
  return !!(readFileIpp(file) && readFileIop(file))
}

/**
 * @param {object} file
 * @param {{ archiveUrlFallback?: string|null, signal?: AbortSignal|null }} [opts]
 * @returns {Promise<boolean>}
 */
export async function hydratePreviewFileGeometry(file, opts = {}) {
  if (!file || !fileNeedsGeometryHydration(file)) {
    return !!(readFileIpp(file) && readFileIop(file))
  }
  if (opts.signal && opts.signal.aborted) return false
  if (!canHydratePreviewFileGeometry(file, opts.archiveUrlFallback)) return false

  const buffer = await fetchPreviewFileDicomBytes(file, opts.archiveUrlFallback)
  if (!buffer) return false
  const geometry = parseSliceGeometryFromDicomBuffer(buffer)
  return stampGeometryOnPreviewFile(file, geometry)
}

function firstFileIppEntry(files) {
  if (!Array.isArray(files)) return null
  for (let i = 0; i < files.length; i++) {
    const ipp = readFileIpp(files[i])
    if (ipp) return { ipp, index: i }
  }
  return null
}

function lastFileIppEntry(files) {
  if (!Array.isArray(files)) return null
  for (let i = files.length - 1; i >= 0; i--) {
    const ipp = readFileIpp(files[i])
    if (ipp) return { ipp, index: i }
  }
  return null
}

/**
 * Extremos IPP del stack usando campos manifest/file (no imagen DICOM en RAM).
 * @param {object[]} files
 * @returns {{ ippFirst: number[], ippLast: number[], zSpacing: number|null, sliceDir: number[]|null }|null}
 */
export function resolveStackIppEndsFromFiles(files) {
  const a = firstFileIppEntry(files)
  if (!a || !files || files.length < 1) return null
  const b = lastFileIppEntry(files)
  if (!b || b.index === a.index) {
    return {
      ippFirst: a.ipp,
      ippLast: a.ipp,
      zSpacing: null,
      sliceDir: null,
    }
  }
  const delta = [b.ipp[0] - a.ipp[0], b.ipp[1] - a.ipp[1], b.ipp[2] - a.ipp[2]]
  const dist = len3(delta)
  const steps = b.index - a.index
  if (!(dist > 1e-6) || !(steps > 0)) {
    return {
      ippFirst: a.ipp,
      ippLast: b.ipp,
      zSpacing: null,
      sliceDir: null,
    }
  }
  const zSpacing = dist / steps
  const sliceDir = normalize3(delta)
  const ippFirst = add3(a.ipp, scale3(sliceDir, -a.index * zSpacing))
  return { ippFirst, ippLast: b.ipp, zSpacing, sliceDir }
}

/**
 * Propaga IOP/FoR comunes y rellena IPP faltantes a lo largo del stack.
 * @param {object[]} files
 * @returns {boolean}
 */
export function spreadStackGeometryFromFiles(files) {
  if (!Array.isArray(files) || files.length === 0) return false

  let sharedIop = null
  let sharedFor = null
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (!f) continue
    if (!sharedIop) sharedIop = readFileIop(f)
    if (!sharedFor) sharedFor = readFileFoR(f)
  }
  if (sharedIop) {
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (f && !readFileIop(f)) f.imageOrientationPatient = sharedIop.slice()
    }
  }
  if (sharedFor) {
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (f && !readFileFoR(f)) f.frameOfReferenceUID = sharedFor
    }
  }

  let sharedSpacing = null
  let sharedRows = null
  let sharedCols = null
  let sharedThickness = null
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (!f) continue
    if (!sharedSpacing && f.pixelSpacing) {
      sharedSpacing = Array.isArray(f.pixelSpacing)
        ? f.pixelSpacing.slice(0, 2)
        : f.pixelSpacing
    }
    if (sharedRows == null && f.rows != null) sharedRows = f.rows
    if (sharedCols == null && f.columns != null) sharedCols = f.columns
    if (sharedThickness == null && f.sliceThickness != null) {
      sharedThickness = f.sliceThickness
    }
  }

  const ends = resolveStackIppEndsFromFiles(files)
  if (!ends || !ends.ippFirst || !ends.sliceDir || !(ends.zSpacing > 0)) {
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (!f) continue
      if (!f.pixelSpacing && sharedSpacing) f.pixelSpacing = sharedSpacing.slice()
      if (f.rows == null && sharedRows != null) f.rows = sharedRows
      if (f.columns == null && sharedCols != null) f.columns = sharedCols
      if (f.sliceThickness == null && sharedThickness != null) {
        f.sliceThickness = sharedThickness
      }
    }
    return !!(sharedIop && firstFileIppEntry(files))
  }

  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (!f || readFileIpp(f)) continue
    f.imagePositionPatient = add3(ends.ippFirst, scale3(ends.sliceDir, i * ends.zSpacing))
  }
  for (let i = 0; i < files.length; i++) {
    const f = files[i]
    if (!f) continue
    if (!f.pixelSpacing && sharedSpacing) f.pixelSpacing = sharedSpacing.slice()
    if (f.rows == null && sharedRows != null) f.rows = sharedRows
    if (f.columns == null && sharedCols != null) f.columns = sharedCols
    if (f.sliceThickness == null && sharedThickness != null) {
      f.sliceThickness = sharedThickness
    }
  }
  return true
}

function pickHydrateIndices(files) {
  const n = files.length
  if (n <= 0) return []
  const need = []
  for (let i = 0; i < n; i++) {
    if (files[i] && fileNeedsGeometryHydration(files[i])) need.push(i)
  }
  if (!need.length) return []
  if (need.length <= 3) return need
  const mid = need[Math.floor(need.length / 2)]
  const set = new Set([need[0], mid, need[need.length - 1]])
  return [...set].sort((a, b) => a - b)
}

/**
 * Descarga headers DICOM mínimos (range zip o cache) y rellena IPP/IOP del stack preview.
 * @param {object[]} files
 * @param {{ archiveUrlFallback?: string|null, signal?: AbortSignal|null }} [opts]
 * @returns {Promise<boolean>}
 */
export async function hydratePreviewStackGeometry(files, opts = {}) {
  if (!Array.isArray(files) || files.length === 0) return false
  const archiveUrlFallback = opts.archiveUrlFallback || null
  if (archiveUrlFallback) {
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (f && !f.archiveUrl) f.archiveUrl = archiveUrlFallback
    }
  }
  spreadStackGeometryFromFiles(files)
  const missing = files.some((f) => fileNeedsGeometryHydration(f))
  if (!missing) return true

  const indices = pickHydrateIndices(files)
  const tasks = []
  for (let i = 0; i < indices.length; i++) {
    const idx = indices[i]
    const file = files[idx]
    if (!file || !fileNeedsGeometryHydration(file)) continue
    if (!canHydratePreviewFileGeometry(file, archiveUrlFallback)) continue
    tasks.push(
      hydratePreviewFileGeometry(file, {
        archiveUrlFallback,
        signal: opts.signal,
      })
    )
  }

  if (tasks.length) {
    await Promise.all(tasks)
  }
  spreadStackGeometryFromFiles(files)
  return files.some((f) => readFileIpp(f) && readFileIop(f))
}
